import { AuthEnvironment } from '@symposium/om-account-lib/om-auth-constants/om-auth-constants';
import { Navigation as navUtil } from '@symposium/ui-utilities';
import { countries, locales } from '@symposium/i18n-support';
import get from 'lodash.get';
import { Wikis16 } from '@carbon/icons-react';
import { LOCALE, PATH_TYPE, MASTHEAD_COOKIE_PREFIX, MENU_ITEM_MAP, myAccountMenuItems, workspaceMenuItem, rhmTermsOfServiceMenuItem, profileMenuItem } from './constants';
export function createItemHref({
  path = '',
  pathType = PATH_TYPE.absolute,
  locale = LOCALE.enUS
}) {
  return pathType === PATH_TYPE.absolute ? path : `/${locale}/${path}`;
}
export function getAccountAPIUrl({
  environment,
  path = ''
}) {
  switch (environment) {
    case AuthEnvironment.DEV_RH:
      return `https://localhost.marketplace.redhat.com:3001${path}`;

    case AuthEnvironment.PROD_RH:
      return `https://marketplace.redhat.com${path}`;

    default:
      return `https://sandbox.marketplace.redhat.com${path}`;
  }
}
export function getMastheadCookieName(environment) {
  switch (environment) {
    case AuthEnvironment.DEV_RH:
      return `${MASTHEAD_COOKIE_PREFIX}-dev`;

    case AuthEnvironment.PROD_RH:
      return `${MASTHEAD_COOKIE_PREFIX}-prod`;

    default:
      return `${MASTHEAD_COOKIE_PREFIX}-sandbox`;
  }
}
export function isEnterpriseMarketplaceEnabled(mastheadCookie) {
  return get(mastheadCookie, ['enterpriseMarketplace', 'enabled'], false);
}
export function isEnterpriseMarketplaceUser(user) {
  if (!isValidUser(user)) {
    return false;
  }

  return get(user, 'currentAccount.enterpriseMarketplace.enabled', false);
}
export function isValidUser(user) {
  if (!user) {
    return false;
  }

  const hasIdProp = Object.prototype.hasOwnProperty.call(user, 'id');
  const hasAccountMapProp = Object.prototype.hasOwnProperty.call(user, 'accountMap');
  const hasCurrentAccountIdProp = Object.prototype.hasOwnProperty.call(user, 'currentAccountId');
  return Boolean(hasIdProp && hasCurrentAccountIdProp && hasAccountMapProp && Object.keys(user.accountMap).length > 0);
}
export function isISVUser(user) {
  if (!user || !user.currentAccountId || !user.accountMap) {
    return false;
  }

  const currentAccount = get(user.accountMap, user.currentAccountId, {});
  return get(currentAccount, 'isVendor', false);
}
export function getWorkspaceMenuItem(workspacePages = []) {
  return { ...workspaceMenuItem,
    submenu: [...workspacePages]
  };
}
export function getAccessiblePageIds({
  accountId,
  accountMap = {}
}) {
  return get(accountMap, [accountId, 'validPages'], []).map(page => get(page, 'sectionKey'));
}
export function createAccountMenuItems(pageIds = []) {
  return pageIds.filter(id => MENU_ITEM_MAP[id]).map(id => MENU_ITEM_MAP[id]);
}
export function createMyAccountMenuItem({
  accountMenuItems = []
}) {
  return [{ ...myAccountMenuItems,
    submenu: accountMenuItems
  }];
}
export function createProfileMenuItems() {
  return [profileMenuItem, rhmTermsOfServiceMenuItem];
}
export function mapItemToHandlers(handlers = {}) {
  const keys = Object.keys(handlers);
  return item => keys.reduce((acc, key) => {
    if (typeof handlers[key] === 'function') {
      acc[key] = event => handlers[key](item, event);
    }

    return acc;
  }, {});
}
export function createSegmentEventData(user, url, label) {
  const account = {
    id: get(user, 'currentAccountId', '')
  };
  return {
    url: url,
    account: account,
    user: user,
    type: 'Header',
    action: 'Clicked on ' + label
  };
}
export function getCurrentAccountName(user) {
  if (user) {
    let accountOptions = get(user, 'accountOptions', []);
    if (accountOptions.length === 0) return '';
    const currentAccount = accountOptions.filter(account => {
      return account.isCurrent;
    })[0];
    return currentAccount.label;
  }

  return '';
}
export function getUserInitials(userName) {
  if (!userName || userName === '') return '';
  const [first, ...rest] = userName.trim().split(' ');
  const last = rest.pop();
  return `${first.charAt(0)}${last && last.charAt(0)}`.toUpperCase();
}
export function createLocaleSelectorItems(locale) {
  var _document;

  const localeCountryCode = locale.replace(/-[a-z]{2}/, cc => cc.toUpperCase());
  const hreflangLinks = typeof document !== 'undefined' && ((_document = document) === null || _document === void 0 ? void 0 : _document.querySelectorAll(`link[rel='alternate'][hreflang]`));
  const linkElements = hreflangLinks && hreflangLinks.length ? [...hreflangLinks] : [{
    hreflang: localeCountryCode
  }];
  const localeSubItems = linkElements.filter(element => {
    return element.hreflang.toLowerCase() in locales;
  }).map(element => {
    const cc = element.hreflang.split('-')[1];
    const label = `${countries[cc].label} (${countries[cc].abbreviation})`;
    const selected = element.hreflang.toLowerCase() === locale;
    return navUtil.createItem({
      ariaLabel: `${label} link`,
      id: element.hreflang,
      label: label,
      pathType: navUtil.PATH_TYPE.absolute,
      path: element.href,
      selected
    });
  }).sort((elementA, elementB) => {
    if (elementA.label < elementB.label) {
      return -1;
    } else if (elementA.label > elementB.label) {
      return 1;
    }

    return 0;
  });
  const activeCountryCode = localeCountryCode.split('-')[1];
  return navUtil.createItem({
    ariaLabel: `${countries[activeCountryCode].abbreviation} link`,
    id: navUtil.NAV_ITEM_ID.localeSelector,
    label: countries[activeCountryCode].abbreviation,
    submenu: localeSubItems,
    icon: Wikis16
  });
}
export default {
  createAccountMenuItems,
  createMyAccountMenuItem,
  createItemHref,
  getAccessiblePageIds,
  getAccountAPIUrl,
  getMastheadCookieName,
  isEnterpriseMarketplaceEnabled,
  isEnterpriseMarketplaceUser,
  isValidUser,
  mapItemToHandlers,
  createSegmentEventData,
  getCurrentAccountName,
  getUserInitials,
  createLocaleSelectorItems
};